import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import clsx from "clsx";
import { Children, FC } from "react";
import { Control, Controller, FieldValues, get, Path, useFormState } from "react-hook-form";
import Select, { Props, components } from "react-select";
import colors from "tailwindcss/colors";

interface StyledSelectProps<T extends FieldValues> extends Props {
  title?: string;
  control: Control<T>;
  name: Path<T>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  isSearchable?: boolean;
  isClearable?: boolean;
}

const { ValueContainer, Placeholder } = components;

const CustomValueContainer: FC<any> = ({ children, ...props }) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {Children.map(children, (child) => (child && child.type !== Placeholder ? child : null))}
    </ValueContainer>
  );
};

const StyledSelect: FC<StyledSelectProps<any>> = ({
  title,
  control,
  name,
  className,
  isSearchable = true,
  isClearable = false,
  ...props
}) => {
  const { errors } = useFormState({ control });
  const translateMessage = useTranslateMessage();
  const errorMessage = get(errors, name + ".message") as unknown as string;

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            {...field}
            {...props}
            onChange={(v) => {
              field.onChange(v);
            }}
            isSearchable={isSearchable}
            isClearable={isClearable}
            placeholder={title}
            components={{
              IndicatorSeparator: () => null,
              ValueContainer: CustomValueContainer,
            }}
            className={clsx([className])}
            value={field.value || null}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: colors.gray[300],
                height: "3.5rem",
                borderRadius: "0.5rem",
              }),
              placeholder: (baseStyles, state) => ({
                ...baseStyles,
                transform: state.hasValue ? "translateY(-1rem)" : "translateY(0)",
                fontSize: "0.75rem",
                color: colors.gray[500],
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                overflow: "visible",
                marginLeft: "0.4rem",
              }),
              singleValue: (baseStyles, state) => ({
                ...baseStyles,
                transform: state.hasValue ? "translateY(0.3rem)" : "translateY(0)",
              }),
              dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                padding: "2px",
              }),
            }}
          />
        )}
      />
      {errorMessage !== "" && (
        <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>
      )}
    </div>
  );
};

export default StyledSelect;
